import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { Print as PrintIcon, Close as CloseIcon } from "@mui/icons-material";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";

interface LeaveType {
  id: number;
  name: string;
}

interface Category {
  id: number;
  name: string;
  description: string;
}

interface LeaveApplicationRow {
  id: number;
  leaveType: string;
  leaveTypeId: number;
  days: number;
  startDate: string;
  endDate: string;
  reason: string;
}

interface Employee {
  sno: number;
  name: string;
  designation: string;
  employeeCode: string;
  departmentName: string;
  leaveByType: { [key: string]: number };
  totalDays: number;
  applications: (LeaveApplicationRow | null)[];
  specialLeave: string | null;
  remarks: string;
}

interface LeaveStatementData {
  departmentName: string;
  month: string;
  year: string;
  leaveTypes: LeaveType[];
  employees: Employee[];
  categories: Category[];
  selectedCategoryId: number | null;
}

interface Department {
  id: number;
  name: string;
  parent_department_id: number | null;
}

interface LeaveStatementReportProps {
  onClose: () => void;
  institutionName?: string;
  facultyName?: string;
  departmentId?: number;
}

const LeaveStatementReport: React.FC<LeaveStatementReportProps> = ({
  onClose,
  institutionName = "University of Vavuniya",
  facultyName,
  departmentId,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportData, setReportData] = useState<LeaveStatementData | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedCategory, setSelectedCategory] = useState<number | "">("");
  const [selectedDepartment, setSelectedDepartment] = useState<number | "">(
    departmentId || "",
  );
  const [categories, setCategories] = useState<Category[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const printRef = useRef<HTMLDivElement>(null);

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i,
  );

  useEffect(() => {
    loadManagedDepartments();
  }, []);

  useEffect(() => {
    if (departments.length > 0 || departmentId) {
      loadReport();
    }
  }, [departments, departmentId]);

  // Update categories when report data changes
  useEffect(() => {
    if (reportData?.categories) {
      setCategories(reportData.categories);
    }
  }, [reportData?.categories]);

  const loadManagedDepartments = async () => {
    try {
      const response = await api.get("/manager/managed-departments");
      setDepartments(response.data || []);
    } catch (err) {
      console.error("Failed to load departments:", err);
      // If API fails, continue without department selector
      setDepartments([]);
    }
  };

  // Extract faculty and department names from the hierarchy
  const getDepartmentHierarchy = () => {
    if (!reportData?.departmentName) {
      return { faculty: facultyName || "Faculty", department: "Department" };
    }

    // Department name format: "Grandparent - Parent - Department"
    const parts = reportData.departmentName.split(" - ").filter(Boolean);

    if (parts.length >= 2) {
      // If we have hierarchy, first part is Faculty, last is Department
      return { faculty: parts[0], department: parts[parts.length - 1] };
    } else if (parts.length === 1) {
      // Single level - use it as department
      return { faculty: facultyName || "Faculty", department: parts[0] };
    }

    return {
      faculty: facultyName || "Faculty",
      department: reportData.departmentName,
    };
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      setError("");
      let url = `/manager/monthly-leave-statement?month=${selectedMonth}&year=${selectedYear}`;
      if (selectedCategory) {
        url += `&categoryId=${selectedCategory}`;
      }
      if (selectedDepartment) {
        url += `&departmentId=${selectedDepartment}`;
      } else if (departmentId) {
        url += `&departmentId=${departmentId}`;
      }
      const response = await api.get(url);
      setReportData(response.data);
      // Set categories from first load
      if (response.data.categories && categories.length === 0) {
        setCategories(response.data.categories);
      }
    } catch (err: any) {
      console.error("Failed to load report:", err);
      setError(err.response?.data?.error || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  // Get category name for display
  const getCategoryName = () => {
    if (!selectedCategory) return "All Staff";
    const cat = categories.find((c) => c.id === selectedCategory);
    return cat ? cat.name : "Staff";
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow popups to print the report");
      return;
    }

    const styles = `
      <style>
        @media print {
          @page {
            size: landscape;
            margin: 10mm;
          }
        }
        
        body {
          font-family: 'Times New Roman', Times, serif;
          padding: 20px;
          margin: 0;
        }
        
        .report-header {
          text-align: center;
          margin-bottom: 20px;
        }
        
        .report-header h1 {
          font-size: 16px;
          font-weight: bold;
          margin: 5px 0;
        }
        
        .report-header h2 {
          font-size: 14px;
          font-weight: bold;
          margin: 5px 0;
        }
        
        .report-header h3 {
          font-size: 13px;
          font-weight: bold;
          margin: 5px 0;
        }
        
        .report-header h4 {
          font-size: 12px;
          font-weight: bold;
          margin: 10px 0;
          text-decoration: underline;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }
        
        th, td {
          border: 1px solid #000;
          padding: 6px 4px;
          text-align: center;
        }
        
        th {
          background-color: #f5f5f5;
          font-weight: bold;
        }
        
        .name-cell {
          text-align: left;
          padding-left: 8px;
        }
        
        .designation-cell {
          text-align: left;
          padding-left: 8px;
        }
        
        .special-leave-cell {
          background-color: #f9f9f9;
          font-style: italic;
        }
        
        .signature-section {
          margin-top: 40px;
          display: flex;
          justify-content: space-between;
        }
        
        .signature-box {
          width: 45%;
        }
        
        .signature-line {
          border-top: 1px dotted #000;
          margin-top: 30px;
          padding-top: 5px;
        }
        
        .leave-type-header {
          font-size: 10px;
        }
        
        .sub-header-row th {
          font-weight: normal;
          font-size: 10px;
        }
      </style>
    `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Leave Statement - ${reportData?.month} ${reportData?.year}</title>
          ${styles}
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Abbreviate leave types for table headers (based on name)
  const getLeaveTypeAbbrev = (name: string): string => {
    const n = name.toLowerCase();
    if (n.includes("casual")) return "Cas";
    if (n.includes("vacation") || n.includes("earned")) return "Vac";
    if (n.includes("medical") || n.includes("sick")) return "Med";
    if (n.includes("duty")) return "Duty";
    if (n.includes("study")) return "Study";
    if (n.includes("maternity")) return "Mat";
    if (n.includes("paternity")) return "Pat";
    return name.substring(0, 3);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading report...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Controls - Not printed */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          alignItems: "center",
          flexWrap: "wrap",
          "@media print": { display: "none" },
        }}
      >
        <TextField
          select
          label="Month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          size="small"
          sx={{ minWidth: 120 }}
        >
          {months.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          size="small"
          sx={{ minWidth: 100 }}
        >
          {years.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Employee Category"
          value={selectedCategory}
          onChange={(e) =>
            setSelectedCategory(
              e.target.value === "" ? "" : Number(e.target.value),
            )
          }
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Categories</MenuItem>
          {categories.map((cat) => (
            <MenuItem key={cat.id} value={cat.id}>
              {cat.name}
            </MenuItem>
          ))}
        </TextField>

        {departments.length > 0 && (
          <TextField
            select
            label="Department"
            value={selectedDepartment}
            onChange={(e) =>
              setSelectedDepartment(
                e.target.value === "" ? "" : Number(e.target.value),
              )
            }
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All Managed Departments</MenuItem>
            {departments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Button variant="contained" onClick={loadReport} size="small">
          Generate
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handlePrint}
          startIcon={<PrintIcon />}
          disabled={!reportData || reportData.employees.length === 0}
        >
          Print Report
        </Button>

        <Button
          variant="outlined"
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ ml: "auto" }}
        >
          Close
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Printable Report Content */}
      <div ref={printRef}>
        <Box className="report-header" sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", fontFamily: "'Times New Roman', serif" }}
          >
            {institutionName}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", fontFamily: "'Times New Roman', serif" }}
          >
            {getDepartmentHierarchy().faculty}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", fontFamily: "'Times New Roman', serif" }}
          >
            {getDepartmentHierarchy().department}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: "bold",
              mt: 1,
              textDecoration: "underline",
              fontFamily: "'Times New Roman', serif",
            }}
          >
            {getCategoryName()} Leave Statement for the Month of{" "}
            {reportData?.month || ""} - {reportData?.year || ""}
          </Typography>
        </Box>

        {reportData && reportData.employees.length > 0 ? (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        fontWeight: "bold",
                        borderRight: "1px solid #ccc",
                        width: 50,
                      }}
                    >
                      S.No
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        fontWeight: "bold",
                        borderRight: "1px solid #ccc",
                        minWidth: 150,
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        fontWeight: "bold",
                        borderRight: "1px solid #ccc",
                        minWidth: 150,
                      }}
                    >
                      Designation
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        fontWeight: "bold",
                        borderRight: "1px solid #ccc",
                        minWidth: 100,
                      }}
                    >
                      From-Until
                    </TableCell>
                    <TableCell
                      colSpan={reportData.leaveTypes.length + 2}
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      Leave taken (No. of Days)
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        fontWeight: "bold",
                        borderLeft: "1px solid #ccc",
                        minWidth: 80,
                      }}
                    >
                      Reason for Leave
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{ fontWeight: "bold", minWidth: 80 }}
                    >
                      Remarks
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {reportData.leaveTypes.map((lt) => (
                      <TableCell
                        key={lt.id}
                        align="center"
                        sx={{ fontWeight: "bold", minWidth: 50 }}
                      >
                        {getLeaveTypeAbbrev(lt.name)}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold", minWidth: 50 }}
                    >
                      Other
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold", minWidth: 50 }}
                    >
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.employees.map((emp) => (
                    <React.Fragment key={emp.employeeCode}>
                      {emp.applications.map((app, appIdx) => (
                        <TableRow key={`${emp.employeeCode}-${appIdx}`}>
                          {appIdx === 0 && (
                            <>
                              <TableCell
                                align="center"
                                rowSpan={emp.applications.length}
                                sx={{ borderRight: "1px solid #ccc" }}
                              >
                                {emp.sno}
                              </TableCell>
                              <TableCell
                                className="name-cell"
                                rowSpan={emp.applications.length}
                                sx={{ borderRight: "1px solid #ccc" }}
                              >
                                {emp.name}
                              </TableCell>
                              <TableCell
                                className="designation-cell"
                                rowSpan={emp.applications.length}
                                sx={{ borderRight: "1px solid #ccc" }}
                              >
                                {emp.designation}
                              </TableCell>
                            </>
                          )}
                          <TableCell
                            align="center"
                            sx={{ borderRight: "1px solid #ccc" }}
                          >
                            {app
                              ? `${formatDate(app.startDate)} - ${formatDate(
                                  app.endDate,
                                )}`
                              : ""}
                          </TableCell>
                          {emp.specialLeave ? (
                            <TableCell
                              colSpan={reportData.leaveTypes.length + 1}
                              align="center"
                              className="special-leave-cell"
                              sx={{
                                fontStyle: "italic",
                                bgcolor: "#f9f9f9",
                                borderRight: "1px solid #ccc",
                              }}
                            >
                              {emp.specialLeave}
                            </TableCell>
                          ) : (
                            <>
                              {reportData.leaveTypes.map((lt) => (
                                <TableCell key={lt.id} align="center">
                                  {app && app.leaveTypeId === lt.id
                                    ? app.days
                                    : ""}
                                </TableCell>
                              ))}
                              <TableCell
                                align="center"
                                sx={{ borderRight: "1px solid #ccc" }}
                              >
                                {/* Other leaves */}
                              </TableCell>
                            </>
                          )}
                          {appIdx === 0 && (
                            <TableCell
                              align="center"
                              rowSpan={emp.applications.length}
                              sx={{
                                fontWeight: "bold",
                                borderRight: "1px solid #ccc",
                                borderLeft: "1px solid #ccc",
                              }}
                            >
                              {emp.totalDays > 0 ? emp.totalDays : ""}
                            </TableCell>
                          )}
                          <TableCell
                            align="center"
                            sx={{ borderRight: "1px solid #ccc" }}
                          >
                            {app?.reason || ""}
                          </TableCell>
                          {appIdx === 0 && (
                            <TableCell
                              align="center"
                              rowSpan={emp.applications.length}
                            >
                              {emp.remarks}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Signature Section */}
            <Box
              className="signature-section"
              sx={{
                mt: 6,
                display: "flex",
                justifyContent: "space-between",
                px: 2,
              }}
            >
              <Box
                className="signature-box"
                sx={{ textAlign: "center", minWidth: 250 }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Prepared by:
                </Typography>
                <Box sx={{ mb: 4, minHeight: 60 }}>
                  {/* Space for signature */}
                </Box>
                <Box sx={{ borderTop: "1px solid #000", pt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {user?.name || "Manager Name"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.85rem", mt: 0.5 }}
                  >
                    Date: {new Date().toLocaleDateString("en-GB")}
                  </Typography>
                </Box>
              </Box>
              <Box
                className="signature-box"
                sx={{ textAlign: "center", minWidth: 250 }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Checked by:
                </Typography>
                <Box sx={{ mb: 4, minHeight: 60 }}>
                  {/* Space for signature */}
                </Box>
                <Box sx={{ borderTop: "1px solid #000", pt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    .................................
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.85rem", mt: 0.5 }}
                  >
                    Date: .................................
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              px: 3,
              bgcolor: "#f8f9fa",
              borderRadius: 2,
              border: "1px dashed #dee2e6",
            }}
          >
            <Box
              component="img"
              sx={{
                width: 80,
                height: 80,
                mb: 2,
                opacity: 0.5,
              }}
              alt="No data"
              src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23999'%3E%3Cpath d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z'/%3E%3C/svg%3E"
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Leave Data Found
            </Typography>
            <Typography color="text.secondary" variant="body2">
              No employees or leave records found for{" "}
              <strong>
                {reportData?.month} {reportData?.year}
              </strong>
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              {selectedCategory
                ? `Selected category may not have employees in your department.`
                : `Try selecting a specific category or different month/year.`}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={loadReport}
            >
              Refresh Data
            </Button>
          </Box>
        )}
      </div>
    </Box>
  );
};

export default LeaveStatementReport;
